import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePortalPosition } from "../TaskManagement/usePortalPosition";
import {
  FaBuilding,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaFileAlt,
  FaSyncAlt,
  FaTimes,
  FaUniversity,
  FaChevronDown,
} from "react-icons/fa";
import Paginations from "../GenericComponents/Pagination";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { fetchProviderInsightsProviders } from "./providerInsightsApi";
import ProviderInsightsApplicationsModal from "./ProviderInsightsApplicationsModal";
import ProviderInsightsListTabs from "./ProviderInsightsListTabs";
import {
  TABLE_HEAD_CLASS,
  TABLE_CELL_CLASS,
  formatNumber,
  filterAndSortProviderInsightsIntakes,
} from "./providerInsightsShared";
import {
  MODULE_PAGE_SHELL_STYLE,
  TASK_PAGE_SHELL_CLASS,
} from "../TaskManagement/workspaceConstants";
import {
  FILTER_CARD_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_SEARCH_CLASS,
} from "../reports/commissions/commissionsConstants";
import "../../styles/tailwind.css";
import "../reports/commissions/commissions-responsive.css";

const FILTER_BTN_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
  boxShadow: "none",
};

const selectClass =
  "flex h-10 w-full items-center rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13px] text-gray-800 shadow-sm outline-none transition hover:border-gray-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function FilterChipBadge({ label, onRemove }) {
  return (
    <span className="inline-flex max-w-[260px] items-center gap-1 rounded-md border border-[#007BFF]/20 bg-[#007BFF]/10 py-1 pl-2.5 pr-1.5 text-[12px] font-medium text-[#007BFF]">
      <span className="truncate" title={label}>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[#007BFF]/70 hover:bg-[#007BFF]/15"
        aria-label={`Remove ${label}`}
        style={FILTER_BTN_RESET}
      >
        <FaTimes size={9} />
      </button>
    </span>
  );
}

function FilterResetButton({ onClick, disabled, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-4 text-[13px] font-medium shadow-sm transition ${
        disabled
          ? "cursor-not-allowed border-[#E5E7EB] bg-white text-gray-400 opacity-50"
          : "cursor-pointer border-[#E5E7EB] bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
      } ${className}`}
      style={FILTER_BTN_RESET}
    >
      <FaSyncAlt size={11} />
      Reset
    </button>
  );
}

function KpiCard({ label, value, icon: Icon, accentBg, accentText, loading }) {
  return (
    <div className="flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${accentBg} ${accentText}`}>
        <Icon size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-gray-700">{label}</p>
        <p className="mt-0.5 text-xl font-bold tabular-nums text-gray-900 sm:text-2xl">
          {loading ? "—" : formatNumber(value)}
        </p>
      </div>
    </div>
  );
}

function IntakeMultiSelect({ intakesList, selectedIds, onChange, labelClass }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const portalStyle = usePortalPosition(triggerRef, open, { placement: "bottom", gap: 6 });

  useEffect(() => {
    const onDoc = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (id) => {
    const sid = String(id);
    const next = selectedIds.map(String);
    if (next.includes(sid)) {
      onChange(next.filter((x) => x !== sid).map(Number));
    } else {
      onChange([...selectedIds, Number(id)]);
    }
  };

  const summary =
    selectedIds.length === 0
      ? "All Intakes"
      : selectedIds.length === 1
        ? intakesList.find((i) => String(i.id) === String(selectedIds[0]))?.title ??
          intakesList.find((i) => String(i.id) === String(selectedIds[0]))?.name ??
          "1 intake"
        : `${selectedIds.length} intakes selected`;

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          style={portalStyle}
          className="max-h-56 overflow-auto rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-lg"
        >
          {intakesList.length === 0 ? (
            <p className="px-3 py-2 text-[13px] text-gray-500">No intakes available.</p>
          ) : (
            intakesList.map((intake) => {
              const checked = selectedIds.map(String).includes(String(intake.id));
              return (
                <label
                  key={intake.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[13px] hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(intake.id)}
                    className="rounded border-gray-300"
                  />
                  <span className="truncate">{intake.title ?? intake.name}</span>
                </label>
              );
            })
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef} className="relative block w-full min-w-0">
      <span className={labelClass}>Intakes</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${selectClass} justify-between gap-2 text-left`}
        style={FILTER_BTN_RESET}
      >
        <span className="truncate">{summary}</span>
        <FaChevronDown size={12} className="shrink-0 opacity-60" />
      </button>
      {menu}
    </div>
  );
}

function CountLink({ count, onClick, className }) {
  const display = formatNumber(count ?? 0);
  const isZero = !count || count <= 0;
  const zeroClass = "text-base font-bold tabular-nums leading-none text-gray-400 sm:text-lg";

  if (!onClick) {
    return (
      <span className={`${zeroClass} ${isZero ? "" : "text-gray-800"} ${className}`}>
        {display}
      </span>
    );
  }

  if (isZero) {
    return <span className={`${zeroClass} ${className}`}>0</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer text-base font-bold tabular-nums leading-none underline decoration-2 underline-offset-[5px] transition hover:opacity-85 sm:text-lg ${className || "text-[#007BFF]"}`}
      style={FILTER_BTN_RESET}
    >
      {display}
    </button>
  );
}

function ProviderInsights() {
  const permissions = useMemo(() => {
    try {
      return getPermissions() || {};
    } catch {
      return {};
    }
  }, []);

  const [listTab, setListTab] = useState("active");
  const [intakeIds, setIntakeIds] = useState([]);
  const [providerSearchInput, setProviderSearchInput] = useState("");
  const [debouncedProviderSearch, setDebouncedProviderSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [filterKey, setFilterKey] = useState(0);

  const [intakesList, setIntakesList] = useState([]);

  const intakesForFilter = useMemo(
    () => filterAndSortProviderInsightsIntakes(intakesList),
    [intakesList],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totals, setTotals] = useState({});
  const [tabCounts, setTabCounts] = useState({ active: 0, inactive: 0 });
  const [providerRows, setProviderRows] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  const [appsModal, setAppsModal] = useState({
    open: false,
    providerId: null,
    providerName: "",
    segment: "reached_coe",
    title: "",
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedProviderSearch(providerSearchInput.trim());
      setFilterKey((k) => k + 1);
    }, 400);
    return () => clearTimeout(t);
  }, [providerSearchInput]);

  useEffect(() => {
    apiUrl.get("/fetchIntakes").then((res) => {
      const list = res?.data?.data ?? res?.data ?? [];
      setIntakesList(Array.isArray(list) ? list : []);
    }).catch(() => setIntakesList([]));
  }, []);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchProviderInsightsProviders({
        list: listTab,
        intakeIds: intakeIds.length > 0 ? intakeIds : undefined,
        page: pagination.currentPage,
        perPage: pagination.perPage,
        providerName: debouncedProviderSearch || undefined,
        location: locationFilter || undefined,
      });
      setTotals(result.totals);
      setTabCounts(result.tabCounts);
      setProviderRows(result.providers);
      setPagination((prev) => ({ ...prev, ...result.pagination }));
    } catch (err) {
      setError(err?.message || "Unable to load provider insights.");
      setTotals({});
      setProviderRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    intakeIds,
    listTab,
    pagination.currentPage,
    pagination.perPage,
    debouncedProviderSearch,
    locationFilter,
  ]);

  useEffect(() => {
    if (permissions.providers !== 1) return;
    loadProviders();
  }, [permissions.providers, loadProviders, filterKey]);

  const bumpFilters = useCallback(() => {
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setFilterKey((k) => k + 1);
  }, []);

  const resetFilters = () => {
    setProviderSearchInput("");
    setDebouncedProviderSearch("");
    setLocationFilter("");
    setIntakeIds([]);
    bumpFilters();
  };

  const hasFiltersActive = useMemo(
    () =>
      Boolean(
        debouncedProviderSearch ||
          locationFilter ||
          intakeIds.length > 0,
      ),
    [debouncedProviderSearch, locationFilter, intakeIds.length],
  );

  const filterChips = useMemo(() => {
    const chips = [];
    intakeIds.forEach((id) => {
      const intake = intakesForFilter.find((i) => String(i.id) === String(id));
      chips.push({
        id: `intake-${id}`,
        type: "intake",
        intakeId: id,
        label: `Intake: ${intake?.title ?? intake?.name ?? id}`,
      });
    });
    if (debouncedProviderSearch) {
      chips.push({ id: "providerSearch", type: "providerSearch", label: `Search: ${debouncedProviderSearch}` });
    }
    if (locationFilter) {
      chips.push({ id: "location", type: "location", label: `Location: ${locationFilter}` });
    }
    return chips;
  }, [intakeIds, intakesForFilter, debouncedProviderSearch, locationFilter]);

  const removeFilterChip = (chip) => {
    if (chip.type === "intake") {
      setIntakeIds((ids) => ids.filter((id) => String(id) !== String(chip.intakeId)));
    }
    if (chip.type === "providerSearch") {
      setProviderSearchInput("");
      setDebouncedProviderSearch("");
    }
    if (chip.type === "location") setLocationFilter("");
    bumpFilters();
  };

  const openApplicationsModal = (row, segment) => {
    const segmentLabel =
      segment === "reached_coe"
        ? "Enrolled"
        : segment === "reached_coe_discontinued"
          ? "Discontinued (was enrolled)"
          : segment === "below_coe"
            ? "Not enrolled"
            : segment === "all"
              ? "Total applications"
              : "Applications";
    setAppsModal({
      open: true,
      providerId: row.provider_id,
      providerName: row.provider_name,
      segment,
      title: `${segmentLabel} — ${row.provider_name}`,
    });
  };

  const handlePagination = (page, newPerPage) => {
    if (newPerPage) {
      setPagination((prev) => ({
        ...prev,
        perPage: parseInt(newPerPage, 10),
        currentPage: 1,
      }));
    } else {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
    setFilterKey((k) => k + 1);
  };

  if (permissions.providers !== 1) {
    return (
      <div className="min-h-full bg-[#f4f6f8] px-3 py-6 sm:px-4 lg:px-6">
        <h1 className="text-xl font-bold text-slate-900">Provider Insights</h1>
        <p className="mt-1 text-sm text-slate-500">You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="tailwind-container w-full">
      <div
        className={`${TASK_PAGE_SHELL_CLASS} provider-insights-shell w-full max-w-full text-left`}
        style={MODULE_PAGE_SHELL_STYLE}
      >
        <div className="flex w-full items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <FaBuilding size={18} />
          </span>
          <div>
            <h1 className="text-xl font-bold text-[#35314d] sm:text-2xl">Provider Insights</h1>
            <p className="mt-0.5 text-[13px] text-gray-500">
              Enrolled = ever reached COE/CAS/LOA or fee paid (status 9, 100, or post-COE), including applications
              now discontinued. Conditional offer alone is not enrolled.
            </p>
          </div>
        </div>

        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {error}
          </div>
        ) : null}

        <div className="provider-insights-kpi-row provider-insights-kpi-row--five mt-3">
          <KpiCard
            label="Total applications"
            value={totals.total_applications}
            icon={FaFileAlt}
            accentBg="bg-gray-100"
            accentText="text-gray-600"
            loading={loading}
          />
          <KpiCard
            label="Enrolled (COE / fee paid)"
            value={totals.enrolled_applications}
            icon={FaCheckCircle}
            accentBg="bg-emerald-50"
            accentText="text-emerald-600"
            loading={loading}
          />
          <KpiCard
            label="Not enrolled"
            value={totals.not_enrolled_applications}
            icon={FaClock}
            accentBg="bg-sky-50"
            accentText="text-sky-600"
            loading={loading}
          />
          <KpiCard
            label="Partnered providers"
            value={tabCounts.active ?? totals.active_providers}
            icon={FaUsers}
            accentBg="bg-indigo-50"
            accentText="text-indigo-600"
            loading={loading}
          />
          <KpiCard
            label="Potential partners"
            value={tabCounts.inactive ?? totals.inactive_providers}
            icon={FaBuilding}
            accentBg="bg-amber-50"
            accentText="text-amber-600"
            loading={loading}
          />
        </div>

        <div className="mt-4 flex justify-center px-2">
          <ProviderInsightsListTabs
            value={listTab}
            tabCounts={tabCounts}
            onChange={(id) => {
              setListTab(id);
              bumpFilters();
            }}
          />
        </div>

        <div className={`provider-insights-filter-card mt-3 ${FILTER_CARD_CLASS}`}>
          <div className="provider-insights-filter-stack">
            <div className="provider-insights-filter-row-main">
              <div className="provider-insights-filter-fields provider-insights-filter-fields--full">
                <IntakeMultiSelect
                  intakesList={intakesForFilter}
                  selectedIds={intakeIds}
                  onChange={(ids) => {
                    setIntakeIds(ids);
                    bumpFilters();
                  }}
                  labelClass={FILTER_LABEL_CLASS}
                />
                <label className="block w-full min-w-0">
                  <span className={FILTER_LABEL_CLASS}>Search provider</span>
                  <input
                    type="search"
                    placeholder="Provider name or ID"
                    className={`${FILTER_SEARCH_CLASS} w-full`}
                    value={providerSearchInput}
                    onChange={(e) => setProviderSearchInput(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {hasFiltersActive ? (
              <div className="provider-insights-filter-footer">
                <div className="provider-insights-filter-chips">
                  <span className="shrink-0 text-[12px] font-medium text-gray-500">Active filters:</span>
                  {filterChips.map((chip) => (
                    <FilterChipBadge
                      key={chip.id}
                      label={chip.label}
                      onRemove={() => removeFilterChip(chip)}
                    />
                  ))}
                </div>
                <FilterResetButton onClick={resetFilters} className="ml-auto" />
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse">
              <thead>
                <tr>
                  <th className={TABLE_HEAD_CLASS}>#</th>
                  <th className={`${TABLE_HEAD_CLASS} min-w-[200px]`}>Provider</th>
                  <th className={`${TABLE_HEAD_CLASS} text-center`}>Total Applications</th>
                  <th className={`${TABLE_HEAD_CLASS} text-center`}>Enrolled</th>
                  <th className={`${TABLE_HEAD_CLASS} text-center`}>Not enrolled</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className={`${TABLE_CELL_CLASS} py-10 text-center text-gray-500`}>
                      Loading…
                    </td>
                  </tr>
                ) : providerRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`${TABLE_CELL_CLASS} py-10 text-center text-gray-500`}>
                      No providers in this list.
                    </td>
                  </tr>
                ) : (
                  providerRows.map((row, idx) => (
                    <tr key={row.provider_id} className="border-b border-gray-100 hover:bg-gray-50/80">
                      <td className={`${TABLE_CELL_CLASS} text-gray-400 tabular-nums`}>
                        {(pagination.currentPage - 1) * pagination.perPage + idx + 1}
                      </td>
                      <td className={`${TABLE_CELL_CLASS} min-w-[200px] max-w-[320px]`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <FaUniversity size={14} />
                          </span>
                          <span
                            className="min-w-0 flex-1 text-[13px] font-semibold leading-snug text-gray-900 break-words whitespace-normal"
                            title={row.provider_name || undefined}
                          >
                            {row.provider_name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className={`${TABLE_CELL_CLASS} text-center`}>
                        <CountLink
                          count={row.total_applications}
                          className="text-[#007BFF]"
                          onClick={() => openApplicationsModal(row, "all")}
                        />
                      </td>
                      <td className={`${TABLE_CELL_CLASS} text-center`}>
                        <CountLink
                          count={row.enrolled_applications}
                          className="text-emerald-700"
                          onClick={() => openApplicationsModal(row, "reached_coe")}
                        />
                      </td>
                      <td className={`${TABLE_CELL_CLASS} text-center`}>
                        <CountLink
                          count={row.not_enrolled_applications}
                          className="text-sky-700"
                          onClick={() => openApplicationsModal(row, "below_coe")}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination.total > 0 ? (
            <div className="border-t border-slate-100 px-2 py-2">
              <Paginations
                currentPage={pagination.currentPage}
                totalItems={pagination.total}
                itemsPerPage={pagination.perPage}
                lastPage={pagination.lastPage}
                perItem={providerRows.length}
                onPageChange={handlePagination}
              />
            </div>
          ) : null}
        </div>

        <ProviderInsightsApplicationsModal
          open={appsModal.open}
          onClose={() => setAppsModal((m) => ({ ...m, open: false }))}
          title={appsModal.title}
          providerId={appsModal.providerId}
          segment={appsModal.segment}
          intakeIds={intakeIds.length > 0 ? intakeIds : undefined}
          location={locationFilter}
        />
      </div>
    </div>
  );
}

export default ProviderInsights;
